import moment from 'moment';

import {getFiscalYearRelativeToDate} from 'nutshell-core/utils/get-fiscal-year-relative-to-date';

import type {Tab as PageControlBarTab} from 'shells/control-bar/page-control-bar';

import type {UserQuotaFragment_quotas} from 'nutshell-graphql-types';

export type MonthValues = Record<number, number | null>;
export type MonthEntry = {month: number; value: number | null};
export type QuarterEntry = {quarter: number; value: number};

export const START_YEAR = getCurrentFiscalYear() - 1;
export const NUM_YEARS = 4;

export const MONTHS_IN_YEAR = 12;
export const MONTHS_IN_QUARTER = 3;

export function getYears(): number[] {
    return Array.from({length: NUM_YEARS}, (_, i) => START_YEAR + i);
}

export function getYearTabs(): PageControlBarTab[] {
    return getYears().map((year) => ({id: String(year), label: String(year)}));
}

export function getFiscalYearStartMonth(): number {
    if (typeof NutshellFrontendSettings !== 'undefined') {
        const fiscalYear = NutshellFrontendSettings.fiscal_year;
        if (typeof fiscalYear === 'string' && fiscalYear.length > 0) {
            const n = parseInt(fiscalYear.split('-')[0] ?? '1', 10);
            if (n >= 1 && n <= 12) {
                return n;
            }
        }
    }

    return 1;
}

export function getFiscalYearEndMonth(): number {
    const fiscalYearStartMonth = getFiscalYearStartMonth();
    return fiscalYearStartMonth === 1 ? 12 : fiscalYearStartMonth - 1;
}

export function getCurrentFiscalYear(): number {
    const fiscalYearStartDate =
        typeof NutshellFrontendSettings !== 'undefined' &&
        typeof NutshellFrontendSettings.fiscal_year === 'string'
            ? NutshellFrontendSettings.fiscal_year
            : '1-1';

    const {endOfFiscalYear} = getFiscalYearRelativeToDate(moment(), fiscalYearStartDate);

    return endOfFiscalYear.year();
}

export function deriveGridData(
    fiscalYearStartMonth: number,
    monthValues: MonthValues
): {
    adjMonths: MonthEntry[];
    quarterValues: QuarterEntry[];
    yearTotal: number;
} {
    const adjMonths: MonthEntry[] = [];
    const quarterValues: QuarterEntry[] = [];
    let quarterValue = 0;

    for (let i = 0; i < MONTHS_IN_YEAR; i++) {
        // Subtract 1 from startMonth since fiscal year start month is 1-indexed.
        // Mod by MONTHS_IN_YEAR to wrap around to the start of the fiscal year.
        // Add 1 to get the 1-indexed month.
        const month = ((fiscalYearStartMonth - 1 + i) % MONTHS_IN_YEAR) + 1;
        const value = monthValues[month] ?? null;
        adjMonths.push({month, value});
        quarterValue += value ?? 0;

        if ((i + 1) % MONTHS_IN_QUARTER === 0) {
            const quarter = Math.floor((i + 1) / MONTHS_IN_QUARTER);
            quarterValues.push({quarter, value: quarterValue});
            quarterValue = 0;
        }
    }

    const yearTotal = adjMonths.reduce((sum, m) => sum + (m.value ?? 0), 0);

    return {adjMonths, quarterValues, yearTotal};
}

export function buildInitialMonthValues(quota: UserQuotaFragment_quotas | undefined): MonthValues {
    const monthAmounts: MonthValues = {};
    const defaultValue = quota ? 0 : null;
    for (let m = 1; m <= 12; m++) {
        monthAmounts[m] = defaultValue;
    }

    if (!quota) {
        return monthAmounts;
    }

    for (const month of quota.months) {
        monthAmounts[month.identifier] = month.value.amount;
    }

    return monthAmounts;
}

/**
 *
 * @param relativeToDate - The current date (moment())
 * @param month - The month to check (1-12)
 * @param calendarYear - The calendar year to check
 * @param fiscalYearStartDate - The fiscal year start date (string like '10-1') -- NutshellFrontendSettings.fiscal_year
 * @returns
 */
export function monthAndYearAreInCurrentFiscalYear(
    relativeToDate: moment.Moment,
    month: number,
    calendarYear: number,
    fiscalYearStartDate: string
): boolean {
    const {startOfFiscalYear, endOfFiscalYear} = getFiscalYearRelativeToDate(
        relativeToDate,
        fiscalYearStartDate
    );

    return moment([calendarYear, month - 1]).isBetween(
        startOfFiscalYear,
        endOfFiscalYear,
        'month',
        '[]'
    );
}

export function getCalendarYear(month: number, year: number, fiscalYearStartMonth: number): number {
    if (fiscalYearStartMonth !== 1 && month >= fiscalYearStartMonth) {
        return year - 1;
    }

    return year;
}

export function getMonthLabel(
    month: number,
    calendarYear: number,
    fiscalYearStartMonth: number
): string {
    if (fiscalYearStartMonth === 1) {
        return moment([calendarYear, month - 1, 1]).format('MMM');
    }

    return moment([calendarYear, month - 1, 1]).format("MMM 'YY");
}

export type User = {
    quotas: {
        fiscalYear: number;
        months: {
            identifier: number;
            value: {
                amount: number;
            };
        }[];
    }[];
    name: string;
};

export function sortUsersByQuotaExistenceAndName(user1: User, user2: User, fiscalYear: number) {
    const user1Quota = user1.quotas?.find((quota) => quota.fiscalYear === fiscalYear);
    const user2Quota = user2.quotas?.find((quota) => quota.fiscalYear === fiscalYear);
    if (user1Quota && user2Quota) {
        return user1.name.localeCompare(user2.name);
    }
    if (user1Quota) {
        return -1;
    }
    if (user2Quota) {
        return 1;
    }

    return 0;
}

export function formatCurrencyDisplay(amount: number | null, currency: string): string {
    if (amount === null) {
        return '';
    }

    return amount.toLocaleString(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
}
